/**
 * Copyright by:
 *
 * by ESS-Ehrhardt Software Solutions & Service GmbH
 * Basselscheider Str. 55
 * D-56281 Emmelshausen
 * +49 (0) 67 42 / 87 27 0
 * info@epg.com
 */
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TaskAttachmentReference } from '@/models/task.model';
import { FileService } from '../../../services/file/file.service';
import { EPGImageFormat, EPGImageSliderItem } from '../epg-image-slider/epg-image-slider.component';
import { attachCompanyNameToUrl } from '../functions';
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import { LogService, Logger } from '@epg/angular-base-lib';

@Component({
  selector: 'app-attachments-display',
  templateUrl: './attachments-display.component.html',
  styleUrls: ['./attachments-display.component.scss'],
  standalone: false
})
export class AttachmentsDisplayComponent implements OnChanges, OnDestroy {
  @Input() attachments?: TaskAttachmentReference[];
  pdfAttachments: TaskAttachmentReference[] = [];
  imageSliderItems: EPGImageSliderItem[] = [];
  faFilePdf = faFilePdf;
  private readonly imageExtensions = ['.jpg', '.jpeg', '.png'];
  private readonly cachedPdfUrls = new Map<string, string>();
  private readonly loadingPdfIds = new Set<string>();
  private log: Logger;

  constructor(
    private fileService: FileService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    logService: LogService
  ) {
    this.log = logService.getLogger(this);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attachments']) {
      this.pdfAttachments = (this.attachments ?? []).filter((f) =>
        f.file_name.toLowerCase().endsWith('.pdf')
      );
      this.imageSliderItems = (this.attachments ?? [])
        .filter((f) => this.imageExtensions.some((ext) => f.file_name.toLowerCase().endsWith(ext)))
        .map((f) => {
          const rawUrl = `${this.fileService.apiEndPoint}/${f.file_id}`;
          const urlWithTenant = attachCompanyNameToUrl(rawUrl);
          const separator = urlWithTenant.includes('?') ? '&' : '?';
          const url = `${urlWithTenant}${separator}returnType=direct`;
          return {
            image: url,
            imageFormat: EPGImageFormat.URL,
            title: f.file_name,
            rotationValue: 0,
            scaleValue: 1
          };
        });
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.cachedPdfUrls.forEach((url) => URL.revokeObjectURL(url));
    this.cachedPdfUrls.clear();
    this.loadingPdfIds.clear();
  }

  openPdf(file: TaskAttachmentReference): void {
    const cachedUrl = this.cachedPdfUrls.get(file.file_id);
    if (cachedUrl) {
      window.open(cachedUrl, '_blank');
      return;
    }

    if (this.loadingPdfIds.has(file.file_id)) {
      return;
    }

    this.loadingPdfIds.add(file.file_id);
    const rawUrl = `${this.fileService.apiEndPoint}/${file.file_id}`;
    const urlWithTenant = attachCompanyNameToUrl(rawUrl);
    const separator = urlWithTenant.includes('?') ? '&' : '?';
    const finalUrl = `${urlWithTenant}${separator}returnType=direct`;
    this.http.get(finalUrl, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const namedPdfBlob = new File([blob], file.file_name, {
          type: blob.type || 'application/pdf'
        });
        const blobUrl = URL.createObjectURL(namedPdfBlob);
        this.cachedPdfUrls.set(file.file_id, blobUrl);
        window.open(blobUrl, '_blank');
      },
      error: (err) => {
        this.loadingPdfIds.delete(file.file_id);
        this.log.error('Failed to open PDF', err);
      },
      complete: () => this.loadingPdfIds.delete(file.file_id)
    });
  }
}
